import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, ImageUp } from "lucide-react";
import { Button } from "../ui/button";
import { uploadVideoFile } from "../../api";

const KINDS = {
  video: {
    exts: ["mp4", "webm", "mov", "m4v"],
    accept: "video/mp4,video/webm,video/quicktime,.mp4,.webm,.mov,.m4v",
    maxMB: 300,
    typeError: "Unsupported file type. Use mp4, webm, mov or m4v.",
    success: "Video uploaded!",
    Icon: Upload,
  },
  image: {
    exts: ["jpg", "jpeg", "png", "webp"],
    accept: "image/jpeg,image/png,image/webp,.jpg,.jpeg,.png,.webp",
    maxMB: 20,
    typeError: "Unsupported image type. Use jpg, png or webp.",
    success: "Thumbnail uploaded!",
    Icon: ImageUp,
  },
};

// Chunked upload button with progress; calls onUploaded(url) when done.
export default function UploadButton({ onUploaded, label = "Upload video", testId = "upload-video-button", kind = "video" }) {
  const inputRef = useRef(null);
  const [progress, setProgress] = useState(null);
  const cfg = KINDS[kind];

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    const ext = file.name.toLowerCase().split(".").pop();
    if (!cfg.exts.includes(ext)) {
      toast.error(cfg.typeError);
      return;
    }
    if (file.size > cfg.maxMB * 1024 * 1024) {
      toast.error(`File too large (max ${cfg.maxMB}MB).`);
      return;
    }

    setProgress(0);
    try {
      const url = await uploadVideoFile(file, setProgress);
      toast.success(cfg.success);
      onUploaded(url);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    } finally {
      setProgress(null);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={cfg.accept}
        className="hidden"
        onChange={handleFile}
        data-testid={`${testId}-input`}
      />
      <Button
        type="button"
        onClick={() => inputRef.current?.click()}
        disabled={progress !== null}
        className="bg-[#2a2a2a] text-white hover:bg-[#3a3a3a] border border-white/20 font-comfortaa text-xs shrink-0"
        data-testid={testId}
      >
        <cfg.Icon size={13} className="mr-1.5" />
        {progress === null ? label : `Uploading ${progress}%`}
      </Button>
    </>
  );
}
